import type { PortalConfig } from '../config/schema.js';

/**
 * Password rules (brief section 9, OWASP ASVS level 2): a minimum length, no
 * composition rules, no personal or company words. The API also refuses
 * common passwords from its list; the web app shows the same messages.
 */

export type PasswordProblem = 'too_short' | 'too_long' | 'personal' | 'common';

export type PasswordPolicy = PortalConfig['auth']['password'];

/** Words a password may never contain, whatever the user. */
const COMPANY_WORDS = ['wasterobotic', 'salesportal'];

/**
 * Words from the user's name and email that a password may not contain.
 * Only words of 4 letters or more count, so short names never block a password.
 */
export function personalWords(user: { email?: string; firstName?: string; lastName?: string }) {
  const local = (user.email ?? '').split('@')[0] ?? '';
  return [user.firstName, user.lastName, local, ...local.split(/[._+-]/)]
    .map((word) => (word ?? '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word.length >= 4);
}

export function passwordProblems(
  password: string,
  policy: PasswordPolicy,
  personal: readonly string[] = [],
): PasswordProblem[] {
  const problems: PasswordProblem[] = [];
  const length = [...password].length;
  if (length < policy.minLength) problems.push('too_short');
  if (length > policy.maxLength) problems.push('too_long');
  const flat = password.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
  if ([...COMPANY_WORDS, ...personal].some((word) => word !== '' && flat.includes(word))) {
    problems.push('personal');
  }
  return problems;
}

export function passwordProblemMessage(problem: PasswordProblem, policy: PasswordPolicy): string {
  switch (problem) {
    case 'too_short':
      return `Use at least ${policy.minLength} characters. A short sentence is easy to remember.`;
    case 'too_long':
      return `Use at most ${policy.maxLength} characters.`;
    case 'personal':
      return 'Do not use your name, your email or the company name in your password.';
    case 'common':
      return 'This password is too common. Choose another one.';
  }
}
